import cx from 'classnames'
import { observer } from 'mobx-react-lite'

import { Credits } from 'blaze-ui/icons/20/Credits'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { AlertTriangle, ArrowRefresh2, InformationCircleContained } from 'blaze-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import dayjs from 'dayjs'

import { useCreditUsage } from 'hooks/useCreditUsage'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { AutoReloadModal } from '../components/AutoReloadModal/AutoReloadModal'
import { PurchaseContentCreditsModal } from '../components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'

import Styles from './CreditsCard.module.scss'
import { AutopilotUpgradeEntryModal } from 'components/AutopilotUpgradeEntryModal/AutopilotUpgradeEntryModal'
import LockOpen3 from 'blaze-ui/icons/24/LockOpen3'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

/**
 * Credits card displaying current balance, renewal info, and auto-reload controls.
 * Shown in billing settings when billing_usage_refresh flag is enabled.
 */
export const CreditsCard = observer(() => {
  const { openModal } = useModals()
  const workspace = useCurrentWorkspace()
  const {
    creditUsage,
    creditsRemaining,
    startDate,
    endDate,
    canAutoReload,
    usageCosts,
    creditsExpiringSoon,
    totalCreditsUsed,
  } = useCreditUsage()
  const { autopilotAddOns, currentSubscription } = useRootStore().tierUsageStore
  const isGenerationV2Flag = useFlipperFlag('generation_v2')
  const { data: brandPlan } = useCurrentBrandPlan({ cacheTime: 1000 * 60 * 5, enabled: isGenerationV2Flag })
  const hasV2Usage =
    (creditUsage?.gen_v2_image_credits_used ?? 0) > 0 || (creditUsage?.gen_v2_video_credits_used ?? 0) > 0
  const isV2 = (isGenerationV2Flag && !!brandPlan?.has_content_strategies) || hasV2Usage

  const autopilotContentCreditsAddon = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]

  const refillDate = endDate ? dayjs(endDate).add(1, 'day') : null
  const isTrialing = currentSubscription?.status === 'trialing'
  const isPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const isCanceledOrCancelAt = currentSubscription?.status === 'canceled' || currentSubscription?.cancel_at
  const showV2Breakdown = isV2 && !isPioneer

  const monthlyCredits = creditUsage?.base_credits_available ?? 0
  const periodCredits = creditUsage?.initial_credits_available ?? 0

  const showExpiration = !isPioneer

  const v1UsageRows = (
    <>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Stylized Images ({usageCosts?.visual_styles_credits_cost ?? 0})
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.visual_styles_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          AI Images ({usageCosts?.ai_image_credits_cost ?? 0})
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.ai_image_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          AI Videos ({usageCosts?.video_credits_cost ?? 0})
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.video_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          AI managed ads ({usageCosts?.ads_credits_cost ?? 0})
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.ads_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Plagiarism checks ({usageCosts?.plagiarism_credits_cost ?? 0})
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.plagiarism_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          All other content ({usageCosts?.content_credits_cost ?? 0})
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.content_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
    </>
  )

  const v2UsageRows = (
    <>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Images
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.gen_v2_image_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Videos
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.gen_v2_video_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Ads
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.ads_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Other
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {(creditUsage?.content_credits_used ?? 0).toLocaleString()}
        </Text>
      </div>
    </>
  )

  const v2CostsByTypeRows = (
    <>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Short form videos
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {usageCosts?.script_video_credits_cost ?? 0}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Feed video posts
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {usageCosts?.animated_video_credits_cost ?? 0}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Carousel posts
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {usageCosts?.carousel_credits_cost ?? 0}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Still image posts
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {usageCosts?.still_image_credits_cost ?? 0}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Stories
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {usageCosts?.still_image_credits_cost ?? 0}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Blogs
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {usageCosts?.blog_post_credits_cost ?? 0}
        </Text>
      </div>
      <div className={Styles.creditBreakdownRow}>
        <Text variant="metadata" color="var(--dark-60)">
          Emails
        </Text>
        <Text variant="metadata" color="var(--dark-60)">
          {usageCosts?.newsletter_credits_cost ?? 0}
        </Text>
      </div>
    </>
  )

  const creditBreakdownContent = (
    <div className={Styles.creditBreakdownTooltipContent}>
      <div className={Styles.popoverContent}>
        <div className={Styles.creditBreakdownSection}>
          <div className={Styles.creditBreakdownRow}>
            <Text variant="metadata" color="var(--dark-90)">
              Credit balance
            </Text>
            <Text variant="metadata" color="var(--dark-90)">
              {creditsRemaining.toLocaleString()}
            </Text>
          </div>
          {showExpiration && (
            <div className={Styles.creditBreakdownRow}>
              <Text variant="metadata" color="var(--dark-60)">
                Expiring in 7 days
              </Text>
              <Text variant="metadata" color="var(--dark-60)">
                {creditsExpiringSoon.toLocaleString()}
              </Text>
            </div>
          )}
          {!isCanceledOrCancelAt && !isTrialing && (
            <div className={Styles.creditBreakdownRow}>
              <Text variant="metadata" color="var(--dark-60)">
                Monthly credits
              </Text>
              <Text variant="metadata" color="var(--dark-60)">
                {monthlyCredits.toLocaleString()}
              </Text>
            </div>
          )}
        </div>
        <div className={Styles.creditBreakdownDivider} />
        <div className={Styles.creditBreakdownSection}>
          <div className={Styles.creditBreakdownSectionHeader}>
            <div className={Styles.creditBreakdownRow}>
              <Text variant="metadata" color="var(--dark-90)">
                Used credits
              </Text>
              <Text variant="metadata" color="var(--dark-60)">
                {totalCreditsUsed.toLocaleString()}
              </Text>
            </div>
            {startDate && endDate && (
              <Text variant="metadata" color="var(--dark-40)">
                From {dayjs(startDate).format('MMM D')} to {dayjs(endDate).format('MMM D')}
              </Text>
            )}
          </div>
          {showV2Breakdown ? v2UsageRows : v1UsageRows}
        </div>
        {showV2Breakdown && (
          <>
            <div className={Styles.creditBreakdownDivider} />
            <div className={Styles.creditBreakdownSection}>
              <Text variant="metadata" color="var(--dark-90)">
                Costs by type
              </Text>
              {v2CostsByTypeRows}
            </div>
          </>
        )}
      </div>
    </div>
  )

  let autoReloadState: 'ON' | 'OFF' | 'PAUSED'
  if (workspace?.auto_reload_credits && !canAutoReload) {
    autoReloadState = 'PAUSED'
  } else if (workspace?.auto_reload_credits) {
    autoReloadState = 'ON'
  } else {
    autoReloadState = 'OFF'
  }

  const openAutoReloadModal = () => {
    openModal(AutoReloadModal)
  }

  const openPurchaseContentCreditsModal = () => {
    if (!autopilotContentCreditsPrice) return
    openModal(PurchaseContentCreditsModal, { price: autopilotContentCreditsPrice }, { newStack: true })
  }

  const handleManageClick = () => {
    if (currentSubscription?.status === 'trialing') {
      openModal(AutopilotPlansModal, {}, { newStack: true })
    } else {
      openAutoReloadModal()
    }
  }

  const cardsContent = () => {
    if (isTrialing || isCanceledOrCancelAt) {
      return `${periodCredits.toLocaleString()} credits included with your trial.`
    }
    if (refillDate) {
      return `${periodCredits.toLocaleString()} credits reset on ${refillDate.format('MMM D, YYYY')}`
    }
    return `${periodCredits.toLocaleString()} credits per month`
  }

  return (
    <div className={Styles.card}>
      <div className={Styles.headerRow}>
        <div className={Styles.headerLeft}>
          <Credits color="var(--green-50)" size={20} className={Styles.creditIcon} />

          <Heading level={5}>{creditsRemaining.toLocaleString()} credits remaining</Heading>
        </div>
        <Tooltip
          label={creditBreakdownContent}
          placement="bottom"
          className={Styles.creditBreakdownTooltip}
          mouseEnterDelay={200}
        >
          <button type="button" className={Styles.seeDetailsButton} aria-label="Credit breakdown">
            <Text variant="secondary" color="var(--dark-60)">
              See details
            </Text>
            <InformationCircleContained size={18} color="var(--dark-60)" className={Styles.infoIcon} />
          </button>
        </Tooltip>
      </div>

      <div className={Styles.divider} />

      <div className={Styles.detailsSection}>
        {creditsExpiringSoon > 0 && (
          <div className={Styles.expiringCredits}>
            <AlertTriangle size={16} color="var(--dark-60)" />
            <Text variant="secondary" color="var(--dark-60)">
              {creditsExpiringSoon.toLocaleString()} credits expiring in 7 days
            </Text>
          </div>
        )}
        <Text variant="secondary" color="var(--dark-60)">
          {cardsContent()}
        </Text>
      </div>

      <div className={Styles.controlsRow}>
        {currentSubscription?.status === 'active' && (
          <div className={Styles.autoReloadControl}>
            <ArrowRefresh2 size={20} color="var(--dark-80)" className={Styles.autoReloadIcon} />
            <Text variant="secondary" className={Styles.autoReloadLabel}>
              Auto-reload
            </Text>
            <div
              className={cx(Styles.autoReloadPill, {
                [Styles.on]: autoReloadState === 'ON',
                [Styles.off]: autoReloadState === 'OFF',
                [Styles.paused]: autoReloadState === 'PAUSED',
              })}
            >
              {autoReloadState}
            </div>
            <Button
              variant="secondary"
              size="md"
              onPress={
                isCanceledOrCancelAt
                  ? () => openModal(AutopilotUpgradeEntryModal, { variant: 'end_of_trial' }, { newStack: true })
                  : handleManageClick
              }
            >
              Manage
            </Button>
          </div>
        )}

        {!isTrialing && !isCanceledOrCancelAt && (
          <Button
            variant="primary"
            size="md"
            onPress={
              isCanceledOrCancelAt
                ? () => openModal(AutopilotUpgradeEntryModal, { variant: 'end_of_trial' }, { newStack: true })
                : openPurchaseContentCreditsModal
            }
            isDisabled={!autopilotContentCreditsPrice}
            className={Styles.addCreditsButton}
            frontIcon={isCanceledOrCancelAt ? LockOpen3 : undefined}
          >
            Add More Credits
          </Button>
        )}
      </div>
    </div>
  )
})
